/**
 * Component to display a hierarchy of nested standards within a subtree as a
 * list of cards with trees of standards and related content.
 */
import {StyleSheet} from "aphrodite";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {HeadingMedium, Title} from "@khanacademy/wonder-blocks-typography";

import {DownloadCSVButton} from "./download-csv-button.tsx";
import {LevelContext, Section} from "./level-context.tsx";
import StandardsTreeLeafRelatedContent from "./standards-tree-leaf-related-content.tsx";
import TitleForDepth from "./title-for-depth.tsx";
import type {Standard} from "./types.ts";

type Props = {
    // The standard at the root of the tree to display.
    standard: Standard;
    subTreeTitle: string;
    layout: "mobile" | "desktop";
};

type NestedProps = {
    standard: Standard;
    depth: number;
    layout: "mobile" | "desktop";
    lastClicked: string;
    setLastClicked: (lastClicked: string) => void;
};

function StandardCardNested({
    standard,
    depth,
    layout,
    lastClicked,
    setLastClicked,
}: NestedProps): React.ReactElement | null {
    // Just in case
    if (depth > 10) {
        return null;
    }

    const standards = standard.standards ?? [];
    const isLeaf = standards.length === 0;

    return (
        <Section>
            <View style={[styles.indented, styles.marginSmallTop]}>
                <TitleForDepth
                    title={standard.standardId}
                    description={standard.description}
                    coverage={standard.coverage}
                    depth={depth}
                    isLeaf={isLeaf}
                    layout={layout}
                    lastClicked={lastClicked}
                    setLastClicked={setLastClicked}
                />
                {isLeaf ? (
                    <StandardsTreeLeafRelatedContent
                        mappedContent={standard.mappedContent ?? []}
                    />
                ) : (
                    standards.map((s) => (
                        <StandardCardNested
                            key={s.standardId}
                            standard={s}
                            depth={depth + 1}
                            layout={layout}
                            lastClicked={lastClicked}
                            setLastClicked={setLastClicked}
                        />
                    ))
                )}
            </View>
        </Section>
    );
}

function GroupLabel({label}: {label: string}): React.ReactElement {
    const level = React.useContext(LevelContext);
    const tag = level <= 6 ? "h" + level : "h6"; // Fallback to h6 for any levels deeper than 6.
    return (
        <HeadingMedium style={styles.marginSmall} tag={tag}>
            {label}
        </HeadingMedium>
    );
}

function StandardsTreeCards({
    standard,
    subTreeTitle,
    layout,
}: Props): React.ReactElement {
    let titleText = `${subTreeTitle}: ${standard.description}`;
    if (subTreeTitle == null || subTreeTitle === "") {
        titleText = standard.description;
    }

    const standards = standard.standards ?? [];
    const [lastClicked, setLastClicked] = React.useState("");

    const groupedStandards = groupStandards(standards);

    return (
        <Section>
            <Title style={styles.marginSmall}>
                {titleText}{" "}
                <DownloadCSVButton standards={standards} title={titleText} />
            </Title>

            {groupedStandards.map((s) => {
                const Container = s.groupLabel ? Section : View;
                return (
                    <Container key={s.groupLabel}>
                        {s.groupLabel && <GroupLabel label={s.groupLabel} />}
                        {s.standards.map((s) => (
                            <View
                                style={[styles.card, styles.marginSmall]}
                                key={s.standardId}
                            >
                                <StandardCardNested
                                    standard={s}
                                    depth={0}
                                    layout={layout}
                                    lastClicked={lastClicked}
                                    setLastClicked={setLastClicked}
                                />
                            </View>
                        ))}
                    </Container>
                );
            })}
        </Section>
    );
}

type GroupOfStandards = {
    groupLabel: string;
    standards: Array<Standard>;
};

function groupStandards(standards: Array<Standard>): Array<GroupOfStandards> {
    const seenLabels: Record<string, boolean> = {};
    const groupLabels = standards
        .map((s) => s.groupLabel || "")
        .filter((label) => {
            if (!seenLabels[label]) {
                seenLabels[label] = true;
                return true;
            }
            return false;
        });

    return groupLabels.map((label) => {
        return {
            groupLabel: label,
            standards: standards.filter((s) => (s.groupLabel || "") === label),
        };
    });
}

const styles = StyleSheet.create({
    card: {
        backgroundColor: color.white,

        border: `1px solid ${color.offBlack16}`,
        borderRadius: spacing.small_12,

        paddingBottom: spacing.small_12,
    },

    indented: {
        // TODO(starin): This should really be marginInlineStart for i18n
        marginLeft: spacing.medium_16,
    },

    marginSmall: {
        margin: spacing.small_12,
    },

    marginSmallTop: {
        marginTop: spacing.small_12,
    },
});

export default StandardsTreeCards;
